import type { Request } from 'express'
import { buildUserKey, extractIp } from './helpers'
import { createRateLimiter } from './middleware'
import type { RateLimiterInstance } from './middleware'
import type { RateLimitOptions } from '../../types'

// ── Strategy aliases ───────────────────────────────────────────────────────────

/**
 * Fixed-window limiter with progressive blocking.
 * Alias for `createRateLimiter({ strategy: 'fixed', …options })`.
 */
export const createFixedRateLimiter = (
  options: Omit<RateLimitOptions<Request>, 'strategy'> = {}
): RateLimiterInstance => createRateLimiter({ ...options, strategy: 'fixed' })

/**
 * @deprecated Renamed to createFixedRateLimiter. Will be removed in v2.
 */
export const createProgressiveRateLimiter = createFixedRateLimiter

/**
 * Sliding-window limiter with progressive blocking.
 * Alias for `createRateLimiter({ strategy: 'sliding', …options })`.
 */
export const createSlidingRateLimiter = (
  options: Omit<RateLimitOptions<Request>, 'strategy'> = {}
): RateLimiterInstance => createRateLimiter({ ...options, strategy: 'sliding' })

// ── User-scoped factories ──────────────────────────────────────────────────────

/**
 * Rate limiter keyed by authenticated user ID (`req.user.id` or `req.user._id`),
 * falling back to the client IP when unauthenticated.
 *
 * Requires auth middleware that populates `req.user` before this runs.
 */
export const createUserRateLimiter = (
  options: RateLimitOptions<Request> = {}
): RateLimiterInstance =>
  createRateLimiter({ ...options, keyGenerator: buildUserKey })

/**
 * Sliding-window, user-scoped limiter.
 */
export const createSlidingUserRateLimiter = (
  options: Omit<RateLimitOptions<Request>, 'strategy'> = {}
): RateLimiterInstance =>
  createRateLimiter({
    ...options,
    strategy: 'sliding',
    keyGenerator: buildUserKey
  })

// ── Endpoint-scoped factories ──────────────────────────────────────────────────

/**
 * Limiter whose key is prefixed with the endpoint name, giving each route its
 * own independent counter even for the same IP or user.
 *
 * @example
 * ```ts
 * // key = "/api/login:203.0.113.5"
 * app.post('/api/login', createEndpointRateLimiter('/api/login', { maxRequests: 5 }))
 * ```
 */
export const createEndpointRateLimiter = (
  endpoint: string,
  options: RateLimitOptions<Request> = {}
): RateLimiterInstance =>
  createRateLimiter({
    ...options,
    keyGenerator: (req: Request) => {
      const base = options.keyGenerator
        ? options.keyGenerator(req)
        : extractIp(req)
      return `${endpoint}:${base}`
    }
  })

/**
 * Sliding-window, endpoint-scoped limiter.
 *
 * @example
 * ```ts
 * app.post('/api/forgot-password',
 *   createSlidingEndpointRateLimiter('/api/forgot-password', { maxRequests: 3 }))
 * ```
 */
export const createSlidingEndpointRateLimiter = (
  endpoint: string,
  options: Omit<RateLimitOptions<Request>, 'strategy'> = {}
): RateLimiterInstance =>
  createRateLimiter({
    ...options,
    strategy: 'sliding',
    keyGenerator: (req: Request) => {
      const base = options.keyGenerator
        ? options.keyGenerator(req)
        : extractIp(req)
      return `${endpoint}:${base}`
    }
  })
